import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ListTodo,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBackgroundTasks, type BackgroundTask } from "@/lib/background-tasks";
import { cn } from "@/lib/utils";

function progressRatio(task: BackgroundTask): number {
  if (task.status === "succeeded") return 1;
  if (task.status === "failed" || task.status === "queued") return 0;
  if (task.progress_total <= 0) return 0;
  return Math.min(1, task.progress_current / task.progress_total);
}

export function BackgroundTasksPanel() {
  const { tasks, activeCount, dismissTask, dismissAll } = useBackgroundTasks();
  const [expanded, setExpanded] = useState(true);
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (tasks.length > 0 && prevCountRef.current === 0) {
      setExpanded(true);
    }
    prevCountRef.current = tasks.length;
  }, [tasks.length]);

  if (tasks.length === 0) return null;

  const finished = tasks.filter((t) => t.status === "succeeded" || t.status === "failed").length;
  const overall =
    tasks.reduce((sum, task) => sum + progressRatio(task), 0) / Math.max(tasks.length, 1);
  const busy = activeCount > 0;
  const title = busy ? "后台任务" : "任务完成";
  const countLabel = `${finished}/${tasks.length}`;

  return (
    <div
      className="overflow-hidden rounded-xl border border-border/80 bg-background/95 shadow-lg backdrop-blur supports-backdrop-filter:bg-background/90"
      role="status"
      aria-live="polite"
      aria-label="后台任务进度"
    >
      {expanded ? (
        <div className="p-3">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ListTodo className="size-4" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{title}</p>
                <div className="flex items-center gap-0.5">
                  <span className="px-1 text-xs text-muted-foreground tabular-nums">{countLabel}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="size-6"
                    aria-label="收起任务面板"
                    aria-expanded={true}
                    onClick={() => setExpanded(false)}
                  >
                    <ChevronUp className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="size-6"
                    aria-label="关闭任务面板"
                    onClick={dismissAll}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                  style={{ width: `${Math.round(overall * 100)}%` }}
                />
              </div>
            </div>
          </div>

          <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto overscroll-contain">
            {tasks.map((task) => (
              <li key={task.id} className="flex items-start gap-2 text-xs">
                <TaskStatusIcon status={task.status} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-1">
                    <p className="truncate font-medium" title={task.title}>
                      {task.title}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="size-5 shrink-0"
                      aria-label="关闭此任务"
                      onClick={() => dismissTask(task.id)}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                  <p className="text-muted-foreground">
                    {task.status === "failed"
                      ? task.error_message || "失败"
                      : task.detail ||
                        (task.status === "queued"
                          ? "排队中…"
                          : task.progress_total > 0
                            ? `${task.progress_current}/${task.progress_total}`
                            : "进行中…")}
                  </p>
                  {task.status === "running" && task.progress_total > 0 ? (
                    <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary/80 transition-[width] duration-150"
                        style={{ width: `${Math.round(progressRatio(task) * 100)}%` }}
                      />
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex w-full items-center gap-1 px-3 py-2.5">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left transition-colors hover:bg-muted/40 rounded-md -mx-1 px-1 py-0.5"
            aria-label={`${title} ${countLabel}，点击展开`}
            aria-expanded={false}
            onClick={() => setExpanded(true)}
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ListTodo className="size-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium">{title}</p>
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground tabular-nums">
                  {countLabel}
                  <ChevronDown className="size-3.5 opacity-70" />
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                  style={{ width: `${Math.round(overall * 100)}%` }}
                />
              </div>
            </div>
          </button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-6 shrink-0"
            aria-label="关闭任务面板"
            onClick={dismissAll}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function TaskStatusIcon({ status }: { status: BackgroundTask["status"] }) {
  if (status === "succeeded") {
    return <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />;
  }
  if (status === "failed") {
    return <XCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />;
  }
  if (status === "running") {
    return <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin text-primary" />;
  }
  return (
    <span
      className={cn("mt-1 size-3.5 shrink-0 rounded-full border border-muted-foreground/40")}
      aria-hidden
    />
  );
}
